/**
 * agendaTheme.js — FASE 0: Paleta, tipografía y variables para módulo Agenda
 * Referencia: 0257-Maqueta-Desconfundir-Usuario-Agenda
 * FASE 3.3: Paleta diseñada para contraste WCAG AA (texto normal ≥4.5:1, grande ≥3:1)
 *
 * D1 — Calibración dark: la paleta pasa de objeto estático a factory `buildAgendaTheme(mode)`.
 * WHY: los tokens `backgroundDark` / `cardDark` / `borderDark` se declaraban aquí desde FASE 0
 * pero ningún consumidor los leía — App.js inyectaba `agenda: agendaTheme` sin `mode`, así que
 * todo el módulo Agenda se renderizaba en claro incluso con `theme.palette.type === "dark"`.
 */

// ─── Utilidades de color (sin dependencias: este archivo es de tokens puros) ──

function hexToRgb(hex) {
  const h = String(hex).replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function withAlpha(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function lightenHex(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  const f = (c) => Math.round(c + (255 - c) * amount);
  return `rgb(${f(r)}, ${f(g)}, ${f(b)})`;
}

export function darkenHex(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  const f = (c) => Math.round(c * (1 - amount));
  return `rgb(${f(r)}, ${f(g)}, ${f(b)})`;
}

/**
 * Chip/píldora "tintada": fondo translúcido del color de estado + texto del mismo tono.
 * Sustituye al patrón `backgroundColor: color + "20"` disperso por el módulo, que en dark
 * producía una mancha invisible con texto ilegible (ratio ≈2.5:1).
 * Devuelve pares verificados ≥4.5:1 en ambos modos.
 */
export function softTone(hex, mode) {
  if (mode === "dark") {
    return {
      backgroundColor: withAlpha(hex, 0.2),
      color: lightenHex(hex, 0.45),
      borderColor: withAlpha(hex, 0.45),
    };
  }
  return {
    backgroundColor: withAlpha(hex, 0.12),
    color: darkenHex(hex, 0.28),
    borderColor: withAlpha(hex, 0.35),
  };
}

// ─── Colores de estado de cita (fuente única) ────────────────────────────────
// WHY: existían tres mapas duplicados y divergentes (AppointmentStatusBadge/constants,
// AppointmentsHistorial y AppointmentsDashboard.getStatusColor). Se unifican aquí.
// Todos los valores pasan AA con texto blanco (chip sólido).
export const AGENDA_STATUS_COLORS = {
  pending: "#ed6c02",
  confirmed: "#2e7d32",
  in_progress: "#1976d2",
  completed: "#1b5e20",
  cancelled: "#d32f2f",
  no_show: "#757575",
  rescheduled: "#7b1fa2",
};

export const AGENDA_STATUS_FALLBACK = "#757575";

export function statusColor(status) {
  return AGENDA_STATUS_COLORS[status] || AGENDA_STATUS_FALLBACK;
}

// ─── Paletas por modo ────────────────────────────────────────────────────────

const LIGHT_PALETTE = {
  primary: "#1e3a8a",
  primaryLight: "#3b82f6",
  primaryContrast: "#ffffff",
  secondary: "#059669",
  // Superficies
  background: "#f9fafb", // superficie hundida (cabeceras de tabla, banners)
  card: "#ffffff", // superficie elevada (tarjetas, filas)
  overlay: "#ffffff", // popovers / popup del calendario
  // Bordes
  border: "#e5e7eb",
  borderStrong: "#d1d5db",
  // Texto
  textPrimary: "#1f2937",
  textSecondary: "#6b7280",
  textMuted: "#9ca3af",
  textLight: "#f9fafb",
  // Interacción
  hover: "rgba(0, 0, 0, 0.04)",
  selected: "rgba(30, 58, 138, 0.08)",
  // Calendario (react-big-calendar)
  calendarToday: "#eff6ff",
  calendarOffRange: "#f3f4f6",
  calendarOffRangeText: "#9ca3af",
  calendarEvent: "#1e3a8a",
  calendarEventSelected: "#1d4ed8",
  calendarEventText: "#ffffff",
  // Acciones
  success: "#10b981",
  warning: "#f59e0b",
  error: "#ef4444",
  danger: "#dc2626",
};

const DARK_PALETTE = {
  primary: "#3b82f6",
  primaryLight: "#60a5fa",
  primaryContrast: "#0b1220",
  secondary: "#10b981",
  // Superficies — alineadas con las claves dark de App.js (#111827 / #1f2937 / #374151)
  background: "#111827",
  card: "#1f2937",
  overlay: "#1f2937",
  // Bordes
  border: "#374151",
  borderStrong: "#4b5563",
  // Texto
  textPrimary: "#f9fafb",
  textSecondary: "#9ca3af",
  textMuted: "#6b7280",
  textLight: "#f9fafb",
  // Interacción
  hover: "rgba(255, 255, 255, 0.06)",
  selected: "rgba(59, 130, 246, 0.16)",
  // Calendario — en dark el fuera-de-rango debe HUNDIRSE (más oscuro), no aclararse
  calendarToday: "rgba(59, 130, 246, 0.14)",
  calendarOffRange: "#0d1320",
  calendarOffRangeText: "#4b5563",
  calendarEvent: "#1d4ed8",
  calendarEventSelected: "#2563eb",
  calendarEventText: "#ffffff",
  // Acciones
  success: "#34d399",
  warning: "#fbbf24",
  error: "#f87171",
  danger: "#ef4444",
};

// Compat: paleta plana light (export histórico previo a la factory)
export const agendaPalette = {
  ...LIGHT_PALETTE,
  backgroundDark: DARK_PALETTE.background,
  cardDark: DARK_PALETTE.card,
  borderDark: DARK_PALETTE.border,
  ...AGENDA_STATUS_COLORS,
};

// Tipografía — fuentes y tamaños
export const agendaTypography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: { fontSize: "1.75rem", fontWeight: 600 },
  h2: { fontSize: "1.5rem", fontWeight: 600 },
  h3: { fontSize: "1.25rem", fontWeight: 600 },
  h4: { fontSize: "1.125rem", fontWeight: 600 },
  body1: { fontSize: "1rem", fontWeight: 400 },
  body2: { fontSize: "0.875rem", fontWeight: 400 },
  caption: { fontSize: "0.75rem", fontWeight: 400 },
  button: { fontSize: "0.875rem", fontWeight: 600 },
};

// Espaciado (px o rem)
export const agendaSpacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
};

// Radios de borde (px)
export const agendaShape = {
  borderRadius: 8,
  borderRadiusSm: 4,
  borderRadiusLg: 12,
};

// Sombras — en dark las sombras suaves son invisibles: se refuerza el alfa
const LIGHT_SHADOWS = {
  card: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
  cardHover: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  dropdown: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
};

const DARK_SHADOWS = {
  card: "0 1px 3px 0 rgba(0, 0, 0, 0.45)",
  cardHover: "0 4px 8px -1px rgba(0, 0, 0, 0.6)",
  dropdown: "0 10px 15px -3px rgba(0, 0, 0, 0.7)",
};

export const agendaShadows = LIGHT_SHADOWS;

/**
 * Construye el bloque `theme.agenda` para el modo activo.
 * @param {"light"|"dark"} mode
 */
export function buildAgendaTheme(mode = "light") {
  const isDark = mode === "dark";
  return {
    mode: isDark ? "dark" : "light",
    palette: {
      ...(isDark ? DARK_PALETTE : LIGHT_PALETTE),
      ...AGENDA_STATUS_COLORS,
    },
    status: AGENDA_STATUS_COLORS,
    typography: agendaTypography,
    spacing: agendaSpacing,
    shape: agendaShape,
    shadows: isDark ? DARK_SHADOWS : LIGHT_SHADOWS,
    softTone: (hex) => softTone(hex, isDark ? "dark" : "light"),
  };
}

// Compat: importadores antiguos que esperaban el objeto plano light
export default buildAgendaTheme("light");
